import React, { useEffect, useRef, useState } from "react";
import { X, LoaderCircle } from "lucide-react";

// Helper for email validation
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const EmailChipsInput = ({
  label,
  name,
  values,
  setValues,
  placeholder,
  disabled,
}) => {
  const [input, setInput] = useState("");
  const inputRef = useRef();

  const handleInputChange = (e) => setInput(e.target.value);

  const addEmail = (email) => {
    const trimmed = email.trim();
    if (trimmed && isValidEmail(trimmed) && !values.includes(trimmed)) {
      setValues([...values, trimmed]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      addEmail(input);
      setInput("");
    }
  };

  const handleBlur = () => {
    addEmail(input);
    setInput("");
  };

  const removeEmail = (idx) => {
    setValues(values.filter((_, i) => i !== idx));
  };

  return (
    <label className="block text-sm font-medium mb-1">
      {label}
      <div
        className="flex flex-wrap items-center border border-gray-300 rounded-md p-2 mt-1 min-h-[40px] bg-white"
        onClick={() => inputRef.current && inputRef.current.focus()}
      >
        {values.map((email, idx) => (
          <span
            key={idx}
            className="flex items-center bg-blue-100 text-blue-800 rounded px-2 py-1 mr-1 mb-1 text-xs"
          >
            {email}
            <button
              type="button"
              className="ml-1 text-blue-500 hover:text-blue-700"
              onClick={() => removeEmail(idx)}
              tabIndex={-1}
              aria-label="Remove email"
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="flex-1 outline-none border-none bg-transparent min-w-[120px] text-sm"
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    </label>
  );
};

const EmailFormModal = ({
  showEmailForm,
  setShowEmailForm,
  emailForm,
  setEmailForm,
  onSendEmail,
  loading,
  defaultEmail,
  reportName,
  currentDate,
}) => {
  // Convert string to array if needed and update form when props change
  useEffect(() => {
    if (showEmailForm && reportName && currentDate) {
      const defaultSubject =
        reportName === "Security Reporting"
          ? `${reportName} - ${currentDate}`
          : `${reportName} - ${currentDate}`;

          const defaultBody = `Hello,

Please see attached ${reportName} dated ${currentDate} ready for your review.

Kind Regards,
Maximal Security`;

      setEmailForm((prev) => ({
        ...prev,
        subject: defaultSubject, // Always update subject when props change
        text: defaultBody, // Always update body when props change
        // Do NOT fall back to any default email when no email is present.
        // If there are no existing "to" emails, leave it empty.
        to: Array.isArray(prev.to)
          ? prev.to
          : prev.to
          ? prev.to
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [],
        cc: Array.isArray(prev.cc)
          ? prev.cc
          : prev.cc
          ? prev.cc
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [],
        bcc: Array.isArray(prev.bcc)
          ? prev.bcc
          : prev.bcc
          ? prev.bcc
              .split(",")
              .map((e) => e.trim())
              .filter(Boolean)
          : [],
      }));
    }
  }, [showEmailForm, reportName, currentDate, setEmailForm, defaultEmail]);

  if (!showEmailForm) return null;

  // Handlers for chips
  const setTo = (arr) => setEmailForm((prev) => ({ ...prev, to: arr }));
  const setCc = (arr) => setEmailForm((prev) => ({ ...prev, cc: arr }));
  const setBcc = (arr) => setEmailForm((prev) => ({ ...prev, bcc: arr }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="bg-white  rounded-md w-full max-w-lg shadow-lg relative">
        <div className="flex p-4 items-center justify-between mb-2 border-b border-black pb-2">
          <h3 className="text-lg font-semibold ">Send E-mail</h3>
          <button
            onClick={() => setShowEmailForm(false)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <EmailChipsInput
            // label="To"
            name="to"
            values={emailForm.to || []}
            setValues={setTo}
            placeholder="email"
            disabled={loading}
          />
          <EmailChipsInput
            // label="CC"
            name="cc"
            values={emailForm.cc || []}
            setValues={setCc}
            placeholder="CC (Comma-separated)"
            disabled={loading}
          />
          <EmailChipsInput
            // label="BCC"
            name="bcc"
            values={emailForm.bcc || []}
            setValues={setBcc}
            placeholder="BCC (Comma-separated)"
            disabled={loading}
          />
          <div className="p-2 rounded-lg border border-blue-600">

          <div >
            <label className="block text-sm font-medium mb-1">
              Subject
         
              <textarea
                // type="text"
                name="subject"
                value={emailForm.subject}
                onChange={(e) =>
                  setEmailForm((prev) => ({
                    ...prev,
                    subject: e.target.value,
                  }))
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                placeholder="Subject"
                // disabled={loading}
                // disabled={true}
                rows={1}
              />
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Body
              <textarea
                name="text"
                value={emailForm.text}
                onChange={(e) =>
                  setEmailForm((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }))
                }
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                placeholder="Message"
                rows={4}
                // disabled={loading}
                // disabled={true}
              />
            </label>
          </div>
          </div>
          <button
            onClick={onSendEmail}
            disabled={
              !(emailForm.to && emailForm.to.length > 0) ||
              !emailForm.subject ||
              !emailForm.text ||
              loading
            }
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md flex justify-center items-center disabled:opacity-50"
          >
            {loading ? (
              <LoaderCircle className="animate-spin w-6 h-6 text-white mr-2" />
            ) : (
              "Send"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailFormModal;
